/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

/** Usage-analytics tracking: POST login row, PUT route on navigation; cached per user per UTC day. */

const STORAGE_ID_KEY = 'metricsCurrentId';
const STORAGE_DATE_KEY = 'metricsCurrentDate';
const STORAGE_PAYLOAD_KEY = 'metricsStartPayload';
const STORAGE_USER_KEY = 'metricsCurrentUser';

interface StartPayload {
  routes: string[];
  loginDateTime: string | null;
}

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
};

const remove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

/** True when value is a positive integer row id (0 and non-numeric mean "no id yet"). */
const isValidId = (value: string | null): boolean =>
  !!value && /^\d+$/.test(value) && Number(value) > 0;

/** Drops the cached row id/day/user (keeps the payload) so the next startMetrics POSTs afresh. */
const clearCachedRow = (): void => {
  remove(STORAGE_ID_KEY);
  remove(STORAGE_DATE_KEY);
  remove(STORAGE_USER_KEY);
};

/** UTC calendar day (YYYY-MM-DD) for the given login timestamp, or today when absent. */
function dayOf(loginDateTime: string | null): string {
  const d = loginDateTime ? new Date(loginDateTime) : new Date();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const date = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}

function loadPayload(): StartPayload | null {
  const raw = read(STORAGE_PAYLOAD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StartPayload;
  } catch {
    remove(STORAGE_PAYLOAD_KEY);
    return null;
  }
}

/** Records the login row for the day and caches its id; reuses same-day cache on refresh. */
export async function startMetrics(
  userId: string,
  routes: string[],
  loginDateTime: string | null,
): Promise<number> {
  if (!userId) return 0;
  const day = dayOf(loginDateTime);

  // Persist payload before cache short-circuit so updateMetrics always has data to send.
  const payload: StartPayload = { routes: [...routes], loginDateTime };
  write(STORAGE_PAYLOAD_KEY, JSON.stringify(payload));

  // Reuse cached row only for same user on same UTC day.
  const cachedId = read(STORAGE_ID_KEY);
  if (isValidId(cachedId) && read(STORAGE_DATE_KEY) === day && read(STORAGE_USER_KEY) === userId) {
    return Number(cachedId);
  }

  const { data } = await apiClient.post<number>(
    `/ediagnostics/v1/user/metrics/${encodeURIComponent(userId)}`,
    payload,
  );
  // Only cache a real row id; failed inserts return 0 and must not poison the cache.
  if (data > 0) {
    write(STORAGE_ID_KEY, String(data));
    write(STORAGE_DATE_KEY, day);
    write(STORAGE_USER_KEY, userId);
  } else {
    clearCachedRow();
  }
  return data;
}

/** Appends the current route to the day's row; no-op until {@link startMetrics} has run. */
export async function updateMetrics(userId: string, fullRoute?: string): Promise<void> {
  if (!userId) return;
  const id = read(STORAGE_ID_KEY);
  const payload = loadPayload();
  // Only update a valid row belonging to this user, and only while it is still today's row -- an id
  // cached on an earlier day no longer exists to the API.
  if (!isValidId(id) || read(STORAGE_USER_KEY) !== userId || !payload) return;
  if (read(STORAGE_DATE_KEY) !== dayOf(null)) return;

  if (fullRoute) {
    payload.routes = [fullRoute];
    write(STORAGE_PAYLOAD_KEY, JSON.stringify(payload));
  }
  await apiClient.put<number>(
    `/ediagnostics/v1/user/metrics/${encodeURIComponent(userId)}`,
    payload,
    { params: { id } },
  );
}

/** Clears the cached id/day/payload (e.g. on logout) so the next login POSTs a fresh row. */
export function clearMetrics(): void {
  remove(STORAGE_ID_KEY);
  remove(STORAGE_DATE_KEY);
  remove(STORAGE_PAYLOAD_KEY);
  remove(STORAGE_USER_KEY);
}
